import DialogWrapper from "@/components/DialogWrapper";
import SignOutButton from "@/components/SignOutButton";
import SiteSettingsSkeleton from "@/components/Skeletons/SiteSettingsSkeleton";
import SettingsForm from "@/components/forms/SettingsForm";
import { Button } from "@/components/ui/button";
import { useTanFetch } from "@/hooks/useTanFetch";
import { UserSettingsWithSources } from "@/types";
import { Link } from "react-router";
import ExportDataForm from "@/components/forms/ExportDataForm";
import CancelSubscriptionDialog from "@/components/CancelSubscriptionDialog";
import { UsageIndicator } from "@/components/UsageIndicator";
import { TierBadge } from "@/components/TierBadge";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { ApiResponse, User } from "@/types";
import { useNavigate } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function SiteSettingsPage() {
  const { data: userSettings, isLoading } = useTanFetch<UserSettingsWithSources>(["settings"], true);
  const { data: userData } = useTanFetch<ApiResponse<User>>(["user"], true);
  const navigate = useNavigate();

  const subscriptionTier = userData?.data?.subscriptionStatus === 'ACTIVE' ? 'ACTIVE' : 'FREE';

  const handleUpgrade = () => {
    navigate('/subscribe');
  };

  if (isLoading || !userSettings) {
    return <SiteSettingsSkeleton />;
  }

  return (
    <div className="max-w-[420px] px-4 py-1 mt-2">
      <h1 className="text-sm font-bold italic">Settings</h1>

      {/* Account & Usage Section */}
      <div className="mt-6 space-y-4">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Account Status</CardTitle>
              <TierBadge tier={subscriptionTier} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <UsageIndicator actionType="purchases" subscriptionTier={subscriptionTier} size="sm" />
              <UsageIndicator actionType="listings" subscriptionTier={subscriptionTier} size="sm" />
              <UsageIndicator actionType="items" subscriptionTier={subscriptionTier} size="sm" />
            </div>

            {subscriptionTier === 'FREE' && (
              <UpgradePrompt
                subscriptionTier={subscriptionTier}
                onUpgrade={handleUpgrade}
                variant="compact"
                className="mt-4"
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <SettingsForm defaultSettings={userSettings} />
      </div>

      <div className="flex flex-col gap-y-4 mt-8 items-start justify-start">
        <span className="flex items-center gap-x-2 text-xs">
          &gt;{" "}
          <Link to="/user-settings" className="text-xs hover:underline">
            Account settings
          </Link>
        </span>

        <DialogWrapper
          title="Export Tables"
          description="Select the tables you would like to export"
          isButton
          content={
            <div>
              <ExportDataForm />
            </div>
          }
          trigger={
            <span className="flex items-center gap-x-2 text-xs">
              &gt;{" "}
              <Button
                variant="link"
                className="text-primary-foreground text-xs p-0 hover:underline h-auto"
              >
                Export data
              </Button>
            </span>
          }
        />

        <DialogWrapper
          title="Cancel subscription"
          description=""
          content={<CancelSubscriptionDialog />}
          isButton
          trigger={
            <span className="flex items-center gap-x-2 text-xs">
              &gt;{" "}
              <Button
                variant="link"
                className="text-primary-foreground text-xs p-0 hover:underline h-auto"
              >
                Cancel subscription
              </Button>
            </span>
          }
        />

        <span className="flex items-center gap-x-2 text-xs">
          &gt;{" "}
          <SignOutButton
            type="link"
            label="Logout"
            className="text-primary-foreground text-xs p-0 hover:underline h-auto"
          />
        </span>
      </div>
    </div>
  );
}
